/**
 * CollaborationPage — operator surface for multi-employee collaboration
 * (DIGEMP-MULTI-EMPLOYEE-COLLABORATION-FRONTEND-059).
 *
 * Lists recent Conversations next to a per-conversation CollaborationBoard: who participated, the
 * current responsibility, each employee's findings, the handoffs between them and the final
 * human-facing response. Reuses the existing agents_view gate — no new permission mechanism, no
 * change to assignment authority.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { conversationsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import CollaborationBoard from "../components/CollaborationBoard";
import { conversationStatusBadgeClass, conversationStatusLabel } from "../components/conversationStatusVocabulary";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("he-IL");
  } catch {
    return "";
  }
}

export default function CollaborationPage() {
  const { id: selectedId } = useParams();
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");

  const { data: conversations = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["collaboration-conversations"],
    queryFn: () => conversationsApi.list({}),
    enabled: canView,
    refetchInterval: 20000,
  });

  const sorted = useMemo(
    () => [...conversations].sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || ""))),
    [conversations],
  );

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בתצוגת שיתוף פעולה</div>;
  }

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div>
        <h1 className="text-xl font-semibold text-gray-800">שיתוף פעולה בין עובדים</h1>
        <p className="text-sm text-gray-500 mt-1">
          מי השתתף בשיחה, מה כל עובד/ת מצא/ה, ההעברות ביניהם והמענה הסופי שהוצג לאדם.
        </p>
      </div>

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          שגיאה בטעינת השיחות
          <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}
      {isLoading && <p className="text-gray-400 text-sm">טוען...</p>}

      <div className="grid gap-4 lg:grid-cols-[360px,1fr] items-start">
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {!isLoading && !isError && sorted.length === 0 && (
            <p className="p-4 text-sm text-gray-400 italic">אין שיחות עדיין</p>
          )}
          <ul className="divide-y divide-gray-100">
            {sorted.map((c) => {
              const participants = c.participants || [];
              const employees = participants.filter((p) => p.role === "agent");
              const hasManager = participants.some((p) => p.role === "manager");
              const selected = c.id === selectedId;
              return (
                <li key={c.id}>
                  <Link
                    to={`/collaboration/${c.id}`}
                    aria-current={selected ? "true" : undefined}
                    className={`block px-4 py-3 ${selected ? "bg-violet-50" : "hover:bg-gray-50"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      {c.ticket_id ? (
                        <span className="text-sm text-blue-600">קריאה #{String(c.ticket_id).slice(0, 8)}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">ללא קריאה</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${conversationStatusBadgeClass(c.status)}`}>
                        {conversationStatusLabel(c.status)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>{employees.length === 0 ? "אין עובדים" : `עובדים: ${employees.length}`}</span>
                      {employees.length >= 2 && (
                        <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">ריבוי עובדים</span>
                      )}
                      {hasManager && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">מנהל</span>
                      )}
                    </div>
                    {employees.length > 0 && (
                      <div className="mt-1 text-xs text-gray-500">
                        {employees.map((p) => p.agent_name || "עובד לא מזוהה").join(", ")}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-400">{formatTime(c.updated_at)}</div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          {selectedId ? (
            <CollaborationBoard conversationId={selectedId} />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-6 text-sm text-gray-400 italic">
              בחרו שיחה מהרשימה כדי לראות את תצוגת שיתוף הפעולה.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
